import React, { useState, useEffect, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'

import { theme } from '../../../lib/theme'
import { IconButton } from '../../components/IconButton'
import { CloseIcon } from '../../icons/close-icon/CloseIcon'

import createContainer from './CreateContainer'

export enum NotificationTypes {
    Info,
    Success,
    Warning,
    Error,
}

export type NotificationModalProps = {
    autoClose?: boolean
    message: ReactNode
    type?: NotificationTypes
    onDismiss: () => void
    index?: number
}

interface NotificationStyleProp {
    type: NotificationTypes
    closing: boolean
}

const fadeIn = keyframes`
    from {
        opacity: 0;
    }

    to {
        transform: 1;
    }
`

const SNotification = styled.div<NotificationStyleProp>`
    position: relative;
    transform: translate(-50%, -50%);
    padding: 20px;
    margin-bottom: 8px;
    background-color: ${(props) => {
        switch (props.type) {
            case NotificationTypes.Success:
                return props.theme.brandColors.CA_Light_Green
            case NotificationTypes.Warning:
                return props.theme.brandColors.CA_Light_Gray
            case NotificationTypes.Error:
                return props.theme.brandColors.CA_Coral
            case NotificationTypes.Info:
            default:
                return props.theme.brandColors.CA_Dark_Gray
        }
    }};
    color: ${(props) => {
        switch (props.type) {
            case NotificationTypes.Success:
            case NotificationTypes.Warning:
            case NotificationTypes.Error:
                return props.theme.brandColors.CA_Black
            case NotificationTypes.Info:
            default:
                return props.theme.brandColors.CA_White
        }
    }};
    box-shadow: rgba(0, 0, 0, 0.1) 0px 5px 15px;
    border-radius: 8px;
    max-width: 625px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    animation: ${fadeIn} 0.2s ease-in-out;
    z-index: 100;

    @media screen and (max-width: 670px) {
        width: 90%;
    }
`

const Notification: React.FC<NotificationModalProps> = ({
    message,
    type = NotificationTypes.Success,
    onDismiss,
    autoClose = true,
    index,
}: NotificationModalProps) => {
    const [closing, setClosing] = useState(false)
    const timeToDismiss = 2000

    useEffect(() => {
        if (closing) {
            const timeoutId = setTimeout(onDismiss, timeToDismiss)

            return () => {
                clearTimeout(timeoutId)
            }
        }
    }, [closing, onDismiss])

    useEffect(() => {
        if (autoClose) {
            const timeoutId = setTimeout(() => setClosing(true), timeToDismiss)

            return () => {
                clearTimeout(timeoutId)
            }
        }
    }, [])

    return createPortal(
        <SNotification type={type} closing={closing} data-testid={'notification-' + index}>
            {message}
            <IconButton onClick={() => onDismiss()} color={theme.brandColors.CA_Black}>
                <CloseIcon />
            </IconButton>
        </SNotification>,
        createContainer()
    )
}

export default Notification
